import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Check, ChevronDown, LoaderCircle } from 'lucide-react';
import type { IndustryStatus } from '@/services/industry-configs.service';
import { STATUS_META, STATUS_ORDER } from './industry-meta';

const MENU_W = 200;
const MENU_H = 150;

/**
 * Build-status pill. With `onChange` it opens a menu to change the status; the
 * menu is portalled to <body> so table and card overflow never clips it.
 */
export function StatusChip({ status, onChange, size = 'md' }: {
  status: IndustryStatus;
  onChange?: (next: IndustryStatus) => Promise<void> | void;
  size?: 'sm' | 'md';
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const meta = STATUS_META[status];
  const Icon = meta.icon;

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const fitsBelow = r.bottom + 6 + MENU_H < window.innerHeight;
    setPos({
      top: fitsBelow ? r.bottom + 6 : r.top - 6 - MENU_H,
      left: Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8)),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); btnRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const pick = async (next: IndustryStatus) => {
    setOpen(false);
    if (next === status || !onChange) return;
    setBusy(true);
    try { await onChange(next); } finally { setBusy(false); }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={busy || !onChange}
        aria-haspopup={onChange ? 'menu' : undefined}
        aria-expanded={onChange ? open : undefined}
        title={onChange ? 'Change build status' : undefined}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o); }}
        className={clsx(
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-semibold transition',
          size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
          meta.chip,
          onChange && 'hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          busy && 'opacity-70',
        )}
      >
        {busy ? <LoaderCircle size={12} className="animate-spin" /> : <Icon size={12} strokeWidth={2.2} />}
        {meta.label}
        {onChange && <ChevronDown size={12} className="-mr-0.5 opacity-60" />}
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{ top: pos.top, left: pos.left, width: MENU_W }}
          className="fade-up fixed z-[60] rounded-xl border border-border bg-surface p-1 shadow-elevated"
        >
          <div className="px-2.5 pb-1 pt-1.5 text-2xs font-medium uppercase tracking-[0.14em] text-text-muted">Build status</div>
          {STATUS_ORDER.map((s) => {
            const m = STATUS_META[s];
            return (
              <button
                key={s}
                type="button"
                role="menuitemradio"
                aria-checked={s === status}
                onClick={() => pick(s)}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-primary transition-colors hover:bg-surface-alt"
              >
                <span className={clsx('h-2 w-2 shrink-0 rounded-full', m.dot)} />
                <span className="flex-1">{m.label}</span>
                {s === status && <Check size={14} className="text-primary" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

/** Three-way segmented control for the detail page header. */
export function StatusSegmented({ status, onChange, busy }: {
  status: IndustryStatus;
  onChange: (next: IndustryStatus) => void;
  busy?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Build status" className="inline-flex gap-1 rounded-xl border border-border bg-surface-alt p-1">
      {STATUS_ORDER.map((s) => {
        const m = STATUS_META[s];
        const active = s === status;
        return (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={busy}
            onClick={() => !active && onChange(s)}
            className={clsx(
              'inline-flex h-8 items-center gap-2 rounded-lg px-3 text-xs font-semibold transition-colors',
              active ? 'bg-surface text-text-primary shadow-sm' : 'text-text-muted hover:text-text-primary',
              busy && 'cursor-wait',
            )}
          >
            <span className={clsx('h-2 w-2 rounded-full', m.dot)} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}
